import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Plus,
  Search,
  Loader2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  Star,
  Stethoscope,
  Scissors,
  GraduationCap,
  Home,
  Store,
  Heart,
  AlertCircle,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Vendors = () => {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [serviceInput, setServiceInput] = useState('');
  const [specialtyInput, setSpecialtyInput] = useState('');
  
  const [newVendor, setNewVendor] = useState({
    name: '',
    vendor_type: 'veterinarian',
    description: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    website: '',
    services: [],
    hours: '',
    accepts_emergency: false,
    image_url: '',
    specialties: []
  });

  useEffect(() => {
    fetchVendors();
  }, [typeFilter]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      let url = `${API}/vendors`;
      if (typeFilter !== 'all') {
        url += `?vendor_type=${typeFilter}`;
      }
      const response = await axios.get(url);
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchVendors();
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vendors/search/${searchQuery}`);
      setVendors(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVendor = async () => {
    if (!newVendor.name.trim() || !newVendor.description.trim()) {
      toast.error('Please fill in required fields');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/vendors`, newVendor);
      setVendors([response.data, ...vendors]);
      setNewVendor({
        name: '', vendor_type: 'veterinarian', description: '', address: '',
        city: '', state: '', zip_code: '', phone: '', email: '', website: '',
        services: [], hours: '', accepts_emergency: false, image_url: '', specialties: []
      });
      setCreateOpen(false);
      toast.success('Vendor added!');
    } catch (error) {
      toast.error('Failed to add vendor');
    } finally {
      setCreating(false);
    }
  };

  const openVendorDetail = async (vendor) => {
    setSelectedVendor(vendor);
    setDetailOpen(true);
    try {
      const response = await axios.get(`${API}/vendors/${vendor.id}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to load reviews');
    }
  };

  const submitReview = async () => {
    if (!newReview.comment.trim()) {
      toast.error('Please write a review');
      return;
    }
    try {
      const response = await axios.post(`${API}/vendors/${selectedVendor.id}/reviews`, newReview);
      setReviews([response.data, ...reviews]);
      setNewReview({ rating: 5, comment: '' });
      // Update vendor rating in list
      const updatedVendor = await axios.get(`${API}/vendors/${selectedVendor.id}`);
      setSelectedVendor(updatedVendor.data);
      setVendors(vendors.map(v => v.id === selectedVendor.id ? updatedVendor.data : v));
      toast.success('Review submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const addService = () => {
    if (serviceInput.trim() && !newVendor.services.includes(serviceInput.trim())) {
      setNewVendor({ ...newVendor, services: [...newVendor.services, serviceInput.trim()] });
      setServiceInput('');
    }
  };

  const removeService = (service) => {
    setNewVendor({ ...newVendor, services: newVendor.services.filter(s => s !== service) });
  };

  const addSpecialty = () => {
    if (specialtyInput.trim() && !newVendor.specialties.includes(specialtyInput.trim())) {
      setNewVendor({ ...newVendor, specialties: [...newVendor.specialties, specialtyInput.trim()] });
      setSpecialtyInput('');
    }
  };

  const removeSpecialty = (specialty) => {
    setNewVendor({ ...newVendor, specialties: newVendor.specialties.filter(s => s !== specialty) });
  };

  const vendorTypes = [
    { value: 'all', label: 'All Types', icon: Store },
    { value: 'veterinarian', label: 'Veterinarian', icon: Stethoscope },
    { value: 'trainer', label: 'Dog Trainer', icon: GraduationCap },
    { value: 'groomer', label: 'Groomer', icon: Scissors },
    { value: 'boarding', label: 'Boarding', icon: Home },
    { value: 'daycare', label: 'Daycare', icon: Heart },
    { value: 'pet_store', label: 'Pet Store', icon: Store },
    { value: 'breeder', label: 'Breeder', icon: Heart },
    { value: 'rescue', label: 'Rescue', icon: Heart },
    { value: 'other', label: 'Other', icon: Store }
  ];

  const getVendorIcon = (type) => {
    const found = vendorTypes.find(v => v.value === type);
    return found ? found.icon : Store;
  };

  const getTypeLabel = (type) => {
    const found = vendorTypes.find(v => v.value === type);
    return found ? found.label : type;
  };

  const renderStars = (rating, size = 'w-4 h-4') => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={`star-${rating}-${i}`} 
        className={`${size} ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-heading font-medium mb-2">Vendor Directory</h1>
            <p className="text-muted-foreground text-lg">
              Find veterinarians, trainers, groomers, and more
            </p>
          </div>

          {user && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-vendor-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Vendor
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add Vendor</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Vendor Name *</Label>
                      <Input
                        placeholder="Happy Paws Vet"
                        value={newVendor.name}
                        onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type *</Label>
                      <Select
                        value={newVendor.vendor_type}
                        onValueChange={(val) => setNewVendor({ ...newVendor, vendor_type: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {vendorTypes.filter(v => v.value !== 'all').map(v => (
                            <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea
                      placeholder="Tell us about this vendor..."
                      value={newVendor.description}
                      onChange={(e) => setNewVendor({ ...newVendor, description: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      placeholder="123 Main St"
                      value={newVendor.address}
                      onChange={(e) => setNewVendor({ ...newVendor, address: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="City"
                      value={newVendor.city}
                      onChange={(e) => setNewVendor({ ...newVendor, city: e.target.value })}
                      className="input-field"
                    />
                    <Input
                      placeholder="State"
                      value={newVendor.state}
                      onChange={(e) => setNewVendor({ ...newVendor, state: e.target.value })}
                      className="input-field"
                    />
                    <Input
                      placeholder="ZIP"
                      value={newVendor.zip_code}
                      onChange={(e) => setNewVendor({ ...newVendor, zip_code: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        placeholder="(555) 123-4567"
                        value={newVendor.phone}
                        onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        placeholder="info@vendor.com"
                        value={newVendor.email}
                        onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                        className="input-field"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input
                      placeholder="https://..."
                      value={newVendor.website}
                      onChange={(e) => setNewVendor({ ...newVendor, website: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Services</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g., Wellness exams"
                        value={serviceInput}
                        onChange={(e) => setServiceInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                        className="input-field flex-1"
                      />
                      <Button type="button" variant="outline" onClick={addService}>Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {newVendor.services.map((s, i) => (
                        <Badge key={s} variant="secondary" className="flex items-center gap-1">
                          {s}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => removeService(s)} />
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Hours</Label>
                    <Input
                      placeholder="Mon-Fri 8am-6pm"
                      value={newVendor.hours}
                      onChange={(e) => setNewVendor({ ...newVendor, hours: e.target.value })}
                      className="input-field"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="emergency"
                      checked={newVendor.accepts_emergency}
                      onChange={(e) => setNewVendor({ ...newVendor, accepts_emergency: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="emergency">Accepts Emergency Cases</Label>
                  </div>

                  <Button 
                    className="w-full btn-primary"
                    onClick={handleCreateVendor}
                    disabled={creating}
                  >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Add Vendor
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 input-field"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-56">
              <SelectValue placeholder="Vendor Type" />
            </SelectTrigger>
            <SelectContent>
              {vendorTypes.map(v => (
                <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Vendors Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : vendors.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Store className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-heading text-xl mb-2">No vendors found</h3>
              <p className="text-muted-foreground mb-4">Be the first to add a vendor!</p>
              {user && (
                <Button className="btn-primary" onClick={() => setCreateOpen(true)}>
                  Add Vendor
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {vendors.map((vendor) => {
              const VendorIcon = getVendorIcon(vendor.vendor_type);
              return (
                <Card 
                  key={vendor.id}
                  className="card-interactive cursor-pointer"
                  onClick={() => openVendorDetail(vendor)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <VendorIcon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-lg font-medium truncate">{vendor.name}</h3>
                        <p className="text-sm text-muted-foreground">{getTypeLabel(vendor.vendor_type)}</p>
                      </div>
                    </div>
                    
                    {vendor.accepts_emergency && (
                      <Badge className="mt-3 bg-red-100 text-red-700">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Emergency
                      </Badge>
                    )}
                    
                    <p className="text-muted-foreground text-sm line-clamp-2 mt-3">
                      {vendor.description}
                    </p>
                    
                    {(vendor.city || vendor.state) && (
                      <div className="flex items-center gap-1 mt-3 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        <span>{[vendor.city, vendor.state].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 mt-3">
                      <div className="flex">{renderStars(Math.round(vendor.average_rating))}</div>
                      <span className="text-sm text-muted-foreground">
                        ({vendor.review_count} reviews)
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Vendor Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedVendor && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">{selectedVendor.name}</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 mt-4">
                  <div className="flex items-center gap-4">
                    <Badge>{getTypeLabel(selectedVendor.vendor_type)}</Badge>
                    {selectedVendor.accepts_emergency && (
                      <Badge className="bg-red-100 text-red-700">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Emergency
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="flex">{renderStars(Math.round(selectedVendor.average_rating), 'w-5 h-5')}</div>
                    <span className="font-medium">{selectedVendor.average_rating.toFixed(1)}</span>
                    <span className="text-muted-foreground">({selectedVendor.review_count} reviews)</span>
                  </div>
                  
                  <p className="text-muted-foreground">{selectedVendor.description}</p>
                  
                  {/* Contact Info */}
                  <div className="space-y-2">
                    {selectedVendor.address && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedVendor.address}, {selectedVendor.city}, {selectedVendor.state} {selectedVendor.zip_code}</span>
                      </div>
                    )}
                    {selectedVendor.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <a href={`tel:${selectedVendor.phone}`} className="text-primary hover:underline">{selectedVendor.phone}</a>
                      </div>
                    )}
                    {selectedVendor.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <a href={`mailto:${selectedVendor.email}`} className="text-primary hover:underline">{selectedVendor.email}</a>
                      </div>
                    )}
                    {selectedVendor.website && (
                      <div className="flex items-center gap-2 text-sm">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        <a href={selectedVendor.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{selectedVendor.website}</a>
                      </div>
                    )}
                    {selectedVendor.hours && (
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedVendor.hours}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Services */}
                  {selectedVendor.services?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Services</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedVendor.services.map((s, i) => (
                          <Badge key={s} variant="secondary">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Reviews */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-4">Reviews</h4>
                    
                    {user && (
                      <div className="bg-muted/50 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm">Your rating:</span>
                          <div className="flex gap-1">
                            {[1,2,3,4,5].map((star) => (
                              <Star
                                key={star}
                                className={`w-5 h-5 cursor-pointer ${star <= newReview.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                                onClick={() => setNewReview({ ...newReview, rating: star })}
                              />
                            ))}
                          </div>
                        </div>
                        <Textarea
                          placeholder="Write your review..."
                          value={newReview.comment}
                          onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                          rows={2}
                          className="mb-2"
                        />
                        <Button size="sm" onClick={submitReview}>Submit Review</Button>
                      </div>
                    )}
                    
                    <div className="space-y-4">
                      {reviews.length === 0 ? (
                        <p className="text-muted-foreground text-sm">No reviews yet</p>
                      ) : (
                        reviews.map((review) => (
                          <div key={review.id} className="border-b pb-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">@{review.username}</span>
                              <div className="flex">{renderStars(review.rating, 'w-3 h-3')}</div>
                            </div>
                            <p className="text-sm text-muted-foreground">{review.comment}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Vendors;
